import { BodyDecoder, BodyEncoder } from '@iotize/tap/client/api';

import { ConverterError } from '../error';

export interface NumberDecoderOptions {
  signed: boolean;
  /**
   * Size of number if bits
   */
  sizeOf: 8 | 16 | 32;
  leastSignificantBitFirst: boolean;
  // converter?: Newable<Uint8Array | Uint32Array | Uint16Array | Int8Array | Int16Array | Int32Array>
}
export interface Newable<T> {
  new (...args: any[]): T;
}

export class NumberConverter
  implements BodyEncoder<number>, BodyDecoder<number>
{
  options: NumberDecoderOptions;

  constructor(options: NumberDecoderOptions) {
    this.options = options;
  }

  /**
   * Number as unsigned 8 bits converter
   * @param lsb true for least significant bit first
   */
  public static uint8(lsb = false) {
    return new NumberConverter({
      signed: false,
      sizeOf: 8,
      leastSignificantBitFirst: lsb,
    });
  }

  /**
   * Number as unsigned 16 bits converter
   * @param lsb true for least significant bit first
   */
  public static uint16(lsb = false) {
    return new NumberConverter({
      signed: false,
      sizeOf: 16,
      leastSignificantBitFirst: lsb,
    });
  }

  /**
   * Number as unsigned 32 bits converter
   * @param lsb true for least significant bit first
   */
  public static uint32(lsb = false): NumberConverter {
    return new NumberConverter({
      signed: false,
      sizeOf: 32,
      leastSignificantBitFirst: lsb,
    });
  }

  /**
   * Number as signed 8 bits converter
   * @param lsb  true for least significant bit first
   */
  static int8(lsb = false): NumberConverter {
    return new NumberConverter({
      signed: true,
      sizeOf: 8,
      leastSignificantBitFirst: lsb,
    });
  }

  /**
   * Number as signed 16 bits converter
   * @param lsb  true for least significant bit first
   */
  static int16(lsb = false): NumberConverter {
    return new NumberConverter({
      signed: true,
      sizeOf: 16,
      leastSignificantBitFirst: lsb,
    });
  }

  /**
   * Number as signed 32 bits converter
   * @param lsb  true for least significant bit first
   */
  static int32(lsb = false): NumberConverter {
    return new NumberConverter({
      signed: true,
      sizeOf: 32,
      leastSignificantBitFirst: lsb,
    });
  }

  public decode(body: Uint8Array): number {
    if (body.length * 8 < this.options.sizeOf) {
      throw ConverterError.unexpectedBufferSize(this.options.sizeOf / 8, body);
    }

    return NumberConverter.fromBytes(
      body,
      this.options.sizeOf / 8,
      this.options.signed,
      this.options.leastSignificantBitFirst,
    );
  }

  public encode(value: number): Uint8Array {
    const maxValue = Math.pow(2, this.options.sizeOf) - 1;
    if (value > maxValue) {
      throw ConverterError.valueTooBigError(value, maxValue);
    }
    const result = new Uint8Array(this.options.sizeOf >> 3);
    for (var i = 0; i < result.length; i++) {
      const decalage = (result.length - i - 1) * 8;
      result[i] = (value >> decalage) & 0xff;
    }
    if (this.options.leastSignificantBitFirst) {
      result.reverse();
    }
    return result;
  }

  public static toOpaqueMsb(
    input: number,
    sizeOf: 8 | 16 | 32 | number,
  ): Uint8Array {
    const maxValue = Math.pow(2, sizeOf) - 1;
    if (input > maxValue) {
      throw ConverterError.valueTooBigError(input, maxValue);
    }
    if (sizeOf === 8) {
      return new Uint8Array([input & 0xff]);
    } else if (sizeOf === 16) {
      return new Uint8Array([(input >> 8) & 0xff, input & 0xff]);
    } else if (sizeOf === 32) {
      return new Uint8Array([
        (input >> 24) & 0xff,
        (input >> 16) & 0xff,
        (input >> 8) & 0xff,
        input & 0xff,
      ]);
    } else {
      throw new Error(`Invalid size ${sizeOf}. Must be 8, 16 or 32`);
    }
  }

  static fromOpaqueMsb(data: Uint8Array, sizeOf?: number): number {
    if (!sizeOf) {
      sizeOf = data.length * 8;
    }
    if (sizeOf === 8) {
      return data[0] & 0xff;
    }
    if (sizeOf === 16) {
      return (data[1] & 0xff) + ((data[0] & 0xff) << 8);
    }
    if (sizeOf === 32) {
      return (
        (data[3] & 0xff) +
        ((data[2] & 0xff) << 8) +
        ((data[1] & 0xff) << 16) +
        ((data[0] & 0xff) << 24)
      );
    }
    throw new Error(`Invalid size ${sizeOf}. Must be 8, 16 or 32`);
  }

  /**
   * @param size size in bytes
   */
  public static fromBytes(
    data: Uint8Array,
    size: number,
    signed: boolean,
    lsb: boolean,
  ) {
    if (lsb) {
      data = data.reverse();
    }
    var result = 0;
    var last = 0;
    var decalage;
    for (var i = 0; i < size; i++) {
      decalage = (size - i - 1) * 8;
      result += ((data[i] & 0xff) << decalage) >>> 0;

      if (last > result && !signed) {
        throw new Error(
          'Overflow. Last value: ' + last + ' but new is ' + result,
        );
      }
      last = result;
    }
    if (signed && (data[0] & 0x80) === 0x80) {
      result = -(Math.pow(2, 8 * size) - result);
    }
    return result;
  }

  // public static toInt(val: number, size: number, signed: boolean) {
  //     var value;
  //     if (signed) {
  //         if (val >= 0)
  //             value = val;
  //         else
  //             value = (val + (1 << size));
  //     }
  //     else {
  //         // unsigned
  //         value = val;
  //     }
  //     return value;
  // }

  // public buildConverter() {
  //     if (this.options.signed) {
  //         switch (this.options.sizeOf) {
  //             case 8:
  //                 this.converter = Int8Array.from;
  //                 break;
  //             case 16:
  //                 this.converter = Int16Array.from;
  //                 break;
  //             case 32:
  //                 this.converter = Int32Array.from;
  //                 break;
  //             default:
  //                 throw new Error("Illegal argument for sizeof");
  //         }
  //     }
  //     else {
  //         switch (this.options.sizeOf) {
  //             case 8:
  //                 this.converter = Uint8Array.from;
  //                 break;
  //             case 16:
  //                 this.converter = Uint16Array.from;
  //                 break;
  //             case 32:
  //                 this.converter = Uint32Array.from;
  //                 break;
  //             default:
  //                 throw new Error("Illegal argument for sizeof");
  //         }
  //     }
  // }
}
